import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useFilterPersistence } from './hooks/useFilterPersistence';
import { extractChipsFromUrlParams } from 'helpers/chipSerialization';
import { ChipTypeEnums } from 'constants/enums';

// Simple function to load temporary asset chips
const loadTemporaryAssetChip = () => {
  try {
    // Check if localStorage is available (it may not be in test environments)
    if (!localStorage || typeof localStorage.getItem !== 'function') {
      return null;
    }
    
    const tempData = localStorage.getItem('temp_asset_drilldown');
    if (!tempData) return null;
    
    const parsedData = JSON.parse(tempData);
    
    // Mark the chip as used instead of immediately removing it
    // This helps prevent race conditions with cleanup
    parsedData.isUsed = true;
    
    // Check if localStorage.setItem is available
    if (typeof localStorage.setItem === 'function') {
      localStorage.setItem('temp_asset_drilldown', JSON.stringify(parsedData));
    }
    
    return parsedData;
  } catch (error) {
    console.warn('Failed to load temporary asset chip:', error);
    // If there was an error, clean up to be safe - but only if localStorage is available
    if (localStorage && typeof localStorage.removeItem === 'function') {
      localStorage.removeItem('temp_asset_drilldown');
    }
    return null;
  }
};

const FilterContext = createContext();

export const useFilters = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
};

// Same context lookup as useFilters but returns null when no FilterProvider
// is mounted. Used by report-subscription helpers that want to render in
// both filter-aware (Defects/Services list pages) and filter-less (User
// profile edit modal) contexts without violating the Rules of Hooks via
// try/catch around useFilters.
export const useOptionalFilters = () => useContext(FilterContext);

export const FilterProvider = ({ config, onFiltersChange, storageKey, children }) => {
  const [filterValues, setFilterValues] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [allFiltersInitialized, setAllFiltersInitialized] = useState(false);
  const [filterPersistSettings, setFilterPersistSettings] = useState({});
  
  // Read and preserve siteAppId from URL params or storage
  // For Client Admin users, use clientUserId instead of siteAppID
  const [siteAppId] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let siteAppIdValue = urlParams.get('siteAppId');

    // If not in URL, get from storage (same pattern as Navbar)
    if (!siteAppIdValue) {
      const userDetail = JSON.parse(sessionStorage.getItem("me")) ||
                         JSON.parse(localStorage.getItem("me")) ||
                         {};
      // Use siteAppID if available, otherwise fall back to clientUserId for Client Admin users
      siteAppIdValue = userDetail.siteAppID;

      // If no siteAppID, try to get clientUserId from localStorage
      if (!siteAppIdValue) {
        const clientUserId = localStorage.getItem("clientUserId") ||
                            sessionStorage.getItem("clientUserId");
        if (clientUserId) {
          try {
            siteAppIdValue = JSON.parse(clientUserId);
          } catch {
            siteAppIdValue = clientUserId;
          }
        }
      }
    }

    return siteAppIdValue;
  });

  // Use the persistence hook
  const persistence = useFilterPersistence(storageKey, siteAppId, config);
  
  // Get initial values from persistence layer (simplified - complex chip logic preserved)
  const computeInitialValues = useCallback(() => {
    if (!config?.filters) return {};

    // Get base initial values from useFilterPersistence
    const baseInitValues = persistence.getInitialValues(config);
    
    // Handle complex chip logic and temporary asset chips (app-specific logic)
    const urlParams = new URLSearchParams(window.location.search);
    const urlChips = extractChipsFromUrlParams(urlParams) || [];
    const tempAssetChip = loadTemporaryAssetChip();
    const isDefectsPage = window.location.pathname.includes('/app/defects');
    const hasPersistedChips = baseInitValues.chips && Array.isArray(baseInitValues.chips) && baseInitValues.chips.length > 0;
    const shouldCleanupAssetChips = isDefectsPage && !tempAssetChip && urlChips.length === 0 && !hasPersistedChips;
    
    // Override chips and department if needed for complex app logic
    if (config.filters.chips) {
      // Start with chips from persistence layer (localStorage) if available
      let combinedChips = [];
      
      // Check if we have chips from persistence layer
      if (baseInitValues.chips && Array.isArray(baseInitValues.chips)) {
        combinedChips = [...baseInitValues.chips];
      }
      
      // If URL has chips, use those instead (URL takes priority)
      if (urlChips.length > 0) {
        combinedChips = [...urlChips];
      }
      
      // Add temporary asset chip if available
      if (tempAssetChip && tempAssetChip.chips && tempAssetChip.chips.length > 0) {
        const assetChip = tempAssetChip.chips[0];
        const isDuplicate = combinedChips.some(chip => 
          chip.chipType === assetChip.chipType && chip.id === assetChip.id
        );
        if (!isDuplicate) {
          combinedChips.push(assetChip);
        }
      }
      
      // Handle cleanup
      if (shouldCleanupAssetChips) {
        combinedChips = combinedChips.filter(chip => chip.chipType !== ChipTypeEnums.SiteAssetID);
      }
      
      baseInitValues.chips = combinedChips;
    }
    
    // Override department from temp asset chip if available  
    if (tempAssetChip && tempAssetChip.department) {
      baseInitValues.department = tempAssetChip.department;
    }
    
    return baseInitValues;
  }, [config, persistence]);

  // Use useState instead of useMemo to ensure initialValues is only computed ONCE on mount
  // This prevents re-computation when filters update the URL during initialization
  const [initialValues] = useState(() => computeInitialValues());

  // Use useCallback to create a stable reference for updateFilter.
  // reportParams (optional) is the flat shape this filter contributes to the
  // backend's report DTOs (DefectSearchPostRequest etc.) - typically plural
  // id arrays. Filters that don't set it fall back to .params at read time,
  // which is correct for primitives (fromDate, defectStatusType, ...).
  const updateFilter = useCallback((filterType, value, params, persistAll = false, reportParams) => {
    const entry = { value, params, reportParams };

    setFilterValues(prev => ({
      ...prev,
      [filterType]: entry,
    }));

    setFilterPersistSettings(prev => ({
      ...prev,
      [filterType]: persistAll
    }));

    const updatedFilters = { ...filterValues, [filterType]: entry };

    onFiltersChange?.(updatedFilters);

    const currentPersistSettings = {
      ...filterPersistSettings,
      [filterType]: persistAll
    };
    persistence.updateUrlAndStorage(updatedFilters, siteAppId, currentPersistSettings);
  }, [filterValues, onFiltersChange, filterPersistSettings, siteAppId, persistence]);

  // Remove asset chips when navigating away from defects page
  useEffect(() => {
    // Function to handle page unload
    const handlePageUnload = () => {
      // Check if we're on a defects page
      if (window.location.pathname.includes('/app/defects')) {
        // Remove any temporary asset chip data
        localStorage.removeItem('temp_asset_drilldown');
      }
    };
    
    // Add event listener for page unload
    window.addEventListener('beforeunload', handlePageUnload);
    
    // Clean up event listener
    return () => {
      window.removeEventListener('beforeunload', handlePageUnload);
    };
  }, []);

  // Check if all configured filters have values (are initialized)
  useEffect(() => {
    if (!config?.filters) return;
    
    const configuredFilters = Object.keys(config.filters);
    
    const filterStatuses = configuredFilters.map(filterKey => {
      const hasFilter = !!filterValues[filterKey];
      let hasValue;
      if (filterKey === 'chips') {
        // For chips, consider it initialized if it exists (even if empty array)
        hasValue = hasFilter && Array.isArray(filterValues[filterKey].value);
      } else {
        hasValue = hasFilter && !!filterValues[filterKey].value;
      }
      return hasValue;
    });
    
    const allInitialized = filterStatuses.every(status => status);
    
    if (allInitialized !== allFiltersInitialized) {
      setAllFiltersInitialized(allInitialized);
    }
  }, [filterValues, config, allFiltersInitialized]);

  // Helper function to check if a filter is required
  const isRequired = useCallback((filterName) => {
    if (!config?.filters) return false;
    
    const filterConfig = config.filters[filterName];
    // If filter config is an object, check required property
    if (typeof filterConfig === 'object') {
      return !!filterConfig.required;
    }
    
    // If filter config is true, it's using defaults (not required)
    return false;
  }, [config]);

  // Helper function to check if a filter should show "Show All" option
  const isShowAll = useCallback((filterName) => {
    if (!config?.filters) return true; // Default to true
    
    const filterConfig = config.filters[filterName];
    // If filter config is an object with showAll property
    if (typeof filterConfig === 'object') {
      // If showAll is explicitly set, use that value, otherwise default to true
      return filterConfig.showAll !== false;
    }
    
    // If filter config is true or undefined, use default (show all)
    return filterConfig !== false;
  }, [config]);

  // Helper function to get initial filter value (initialValues already contains URL + localStorage, then default)
  const getInitialFilterValue = useCallback((filterType, availableOptions, defaultValue, customFindFn) => {
    // initialValues already contains URL params (priority 1) and localStorage values (priority 2)
    let storedValue = initialValues[filterType];
    
    // Special handling for stored 'all' values - convert to empty string for lookup
    if (storedValue === 'all') {
      // If stored value is 'all', look for the item with id: ''
      storedValue = '';
    }
    
    if (storedValue !== undefined && availableOptions.length > 0) {
      // Use custom find function if provided, otherwise fall back to simple equality
      const findFn = customFindFn || ((items, id) => items.find(item => item.id == id));
      const foundOption = findFn(availableOptions, storedValue);
      if (foundOption) {
        return foundOption;
      }
    }
    
    // Fall back to default value if no stored value or not found in options
    return defaultValue;
  }, [initialValues]);

  // Check if all required filters are set
  useEffect(() => {
    if (!config || !config.filters) return;
    
    // Get the list of required filter types
    const requiredFilters = Object.keys(config.filters)
      .filter(filterName => isRequired(filterName));
    
    // Check if all required filters have values and specific checks for service filter
    const allFiltersSet = requiredFilters.every(filter => {
      if (filter === 'service') {
        // For service filter, check if serviceId exists and is not empty
        return filterValues[filter]?.value?.id && filterValues[filter]?.params?.serviceId;
      }
      return !!filterValues[filter];
    });
    setIsReady(allFiltersSet);
  }, [filterValues, config, isRequired]);

  // Helper to build the backend report DTO shape from the current filters.
  // Each filter owns its translation: it sets .reportParams (alongside .params)
  // when it knows the report shape differs from the URL/list shape - e.g. the
  // ChipsFilter flattens chips into typed id arrays, the TimeframeFilter
  // renames startDate/endDate to fromDate/toDate. Filters that don't need a
  // different shape (primitive scalars like defectStatusType) just fall back
  // to .params.
  //
  // No filter-type knowledge in this helper. Adding a new filter that needs
  // a custom report shape means setting .reportParams in that filter
  // component; this picks it up automatically.
  const buildReportFilterContext = useCallback(() => {
    const result = {};
    for (const filterData of Object.values(filterValues)) {
      if (!filterData) continue;
      const shape = filterData.reportParams ?? filterData.params;
      if (!shape || typeof shape !== "object") continue;
      for (const [k, v] of Object.entries(shape)) {
        if (v === undefined || v === "" || v === null) continue;
        if (Array.isArray(v) && v.length === 0) continue;
        result[k] = v;
      }
    }
    return result;
  }, [filterValues]);

  // Helper to build URL params from filter values - memoized with useCallback
  const buildUrlParams = useCallback(() => {
    const params = {};
        
    // Process each filter's params
    Object.entries(filterValues).forEach(([filterType, filterData]) => {
      
      if (filterData.params) {
        Object.entries(filterData.params).forEach(([paramKey, paramValue]) => {
          // Only add params with actual values (not empty strings)
          if (paramValue !== undefined && paramValue !== '') {
            params[paramKey] = paramValue;
          }
        });
      }
      
      // Specifically handle timeframe filter to ensure dates are included
      if (filterType === 'timeframe' && filterData.value) {
        if (filterData.value.fromDate && !params.fromDate) {
          params.fromDate = filterData.value.fromDate;
        }
        if (filterData.value.toDate && !params.toDate) {
          params.toDate = filterData.value.toDate;
        }
      }
      
      // Specifically handle department filter to ensure department ID is included
      if (filterType === 'department' && filterData.value?.id && !params.siteDepartmentId) {
        params.siteDepartmentId = filterData.value.id;
      }
    });
    
    return params;
  }, [filterValues]);


  // URL and storage updates now happen immediately in updateFilter

  // Helper function to remove asset chips from filter values
  const cleanupAssetChips = useCallback(() => {
    setFilterValues(prev => {
      // Check if we have chips
      if (!prev.chips || !prev.chips.value || !Array.isArray(prev.chips.value)) {
        return prev;
      }
      
      // Filter out asset chips
      const newChips = prev.chips.value.filter(chip => chip.chipType !== ChipTypeEnums.SiteAssetID);
      
      // If no changes, return previous state
      if (newChips.length === prev.chips.value.length) {
        return prev;
      }
      
      // Return updated state
      return {
        ...prev,
        chips: {
          ...prev.chips,
          value: newChips
        }
      };
    });
  }, []);

  return (
    <FilterContext.Provider value={{ 
      filterValues, 
      updateFilter,
      isReady,
      allFiltersInitialized,
      buildUrlParams,
      buildReportFilterContext,
      isRequired,
      isShowAll,
      initialValues,
      getInitialFilterValue,
      config,
      storageKey,
      siteAppId,
      persistence,
      cleanupAssetChips
    }}>
      {children}
    </FilterContext.Provider>
  );
}; 
